'use client';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import gsap from 'gsap';

interface MenuLink {
    name: string;
    href: string;
}

interface MobileMenuProps {
    links: MenuLink[];
    activeNav: string;
    setActiveNav: (value: string) => void;
    pathname: string;
}

const MobileMenu = ({ links, activeNav, setActiveNav, pathname }: MobileMenuProps) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const router = useRouter();
    const linksRef = useRef<(HTMLLIElement | null)[]>([]);

    const handleNavClick = (href: string) => {
        if (href.startsWith('#')) {
            const targetId = href.slice(1);

            if (pathname === '/') {
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                    const y =
                        targetElement.getBoundingClientRect().top +
                        window.scrollY -
                        80; // navbar height
                    window.scrollTo({ top: y, behavior: 'smooth' });
                    setActiveNav(href);
                }
            } else {
                router.push('/' + href);
            }
        } else {
            router.push(href);
            setActiveNav(href);
        }
        setIsMenuOpen(false);
    };

    // Animate links when menu opens
    useEffect(() => {
        if (isMenuOpen && linksRef.current.length) {
            gsap.fromTo(
                linksRef.current,
                { y: 30, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.4,
                    stagger: 0.2,
                    
                    ease: 'power3.out',
                }
            );
        }
    }, [isMenuOpen]);

    return (
        <>
            {/* Burger button */}
            <div className="absolute top-0 z-[4]">
                <button
                    className="group size-12 absolute top-3 right-0 z-[2]"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                    <span
                        className={`inline-block w-3/5 h-0.5 bg-white rounded-full absolute left-1/2 -translate-x-1/2 top-1/2 duration-300 -translate-y-[5px]
              ${isMenuOpen ? 'rotate-45 -translate-y-1/2' : 'md:group-hover:rotate-12'}`}
                    />
                    <span
                        className={`inline-block w-3/5 h-0.5 bg-white rounded-full absolute left-1/2 -translate-x-1/2 top-1/2 duration-300 translate-y-[5px]
              ${isMenuOpen ? '-rotate-45 -translate-y-1/2' : 'md:group-hover:-rotate-12'}`}
                    />
                </button>
            </div>

            {/* Overlay */}
            <div
                className={`overlay fixed inset-0 z-[2] bg-black/70 transition-all duration-150 
          ${!isMenuOpen ? 'opacity-0 invisible pointer-events-none' : ''}`}
                onClick={() => setIsMenuOpen(false)}
            ></div>

            {/* Drawer */}
            <div
                className={`fixed top-0 right-0 h-[100dvh] w-[300px] max-w-[calc(100vw-3rem)] z-[3] gap-y-14 
          flex flex-col lg:justify-center py-10 transform transition-transform duration-700 bg-[#1A1A23]
          ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                <div className="grow flex md:items-center w-full">
                    <div className="flex gap-10 lg:justify-between max-lg:flex-col w-full">
                        <div>
                            <p className="text-white text-2xl font-bold mb-5 mt-20 mx-10">MENU</p>
                            <div className='w-full bg-yellow-400 h-1 mb-5 rounded-full' />
                            <ul className="space-y-5 mx-10">
                                {links.map((e, idx) => (
                                    <li
                                        key={e.name}
                                        ref={(el) => {(linksRef.current[idx] = el)}}
                                        className="opacity-0 translate-y-4"
                                    >
                                        <button
                                            onClick={() => handleNavClick(e.href)}
                                            className={`group text-xl flex items-center gap-3 font-medium tracking-wide transition-colors duration-200
                                            ${activeNav === e.href
                                                    ? 'text-yellow-400'
                                                    : 'text-white hover:text-yellow-500'}`}
                                        >
                                            <span className="relative">
                                                {e.name}
                                                {activeNav === e.href && (
                                                    <span className="absolute left-0 -bottom-1 h-[2px] w-full bg-yellow-400 rounded" />
                                                )}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="w-full max-w-[300px] mx-8 text-white">
                    <p className="text-muted-white mb-4">GET IN TOUCH</p>
                    <a href="mailto:contact@evonixventures.com">contact@evonixventures.com</a>
                </div>
            </div>
        </>
    );
};

export default MobileMenu;
